import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import KPICard from "@/components/KPICard";
import { fmtMoney } from "@/lib/format";
import { TrendingUp, TrendingDown, DollarSign, AlertTriangle, Truck, Search, Trophy } from "lucide-react";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function pickWindow() {
    const now = new Date();
    const opts = [];
    for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        opts.push({
            value: `${d.getFullYear()}-${d.getMonth() + 1}`,
            label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}`,
            year: d.getFullYear(),
            month: d.getMonth() + 1,
        });
    }
    return opts;
}

export default function Profitability() {
    const windows = useMemo(pickWindow, []);
    const [win, setWin] = useState(windows[0].value);
    const [truckReport, setTruckReport] = useState(null);
    const [profitReport, setProfitReport] = useState(null);
    const [search, setSearch] = useState("");

    useEffect(() => {
        const w = windows.find((x) => x.value === win);
        if (!w) return;
        Promise.all([
            api.get("/reports/truck-costs", { params: { year: w.year, month: w.month } }),
            api.get("/reports/customer-profitability", { params: { year: w.year, month: w.month } }),
        ]).then(([tr, pr]) => {
            setTruckReport(tr.data);
            setProfitReport(pr.data);
        });
    }, [win, windows]);

    const rows = profitReport?.rows || [];
    const totalRevenue = rows.reduce((s, r) => s + (r.revenue || 0), 0);
    const totalCost = rows.reduce((s, r) => s + (r.total_cost || 0), 0);
    const totalProfit = totalRevenue - totalCost;
    const marginPct = totalRevenue ? Math.round((totalProfit / totalRevenue) * 1000) / 10 : 0;
    const losers = rows.filter((r) => r.revenue > 0 && r.profit < 0);
    const winners = rows.filter((r) => r.profit > 0).sort((a, b) => b.profit - a.profit).slice(0, 5);

    const filtered = rows.filter((r) => {
        if (!search) return true;
        return (r.customer_name || "").toLowerCase().includes(search.toLowerCase());
    });

    return (
        <div data-testid="profitability-page">
            <PageHeader
                title="Profitability"
                subtitle="Revenue minus allocated cost — per truck and per customer."
                actions={
                    <Select value={win} onValueChange={setWin}>
                        <SelectTrigger className="w-40" data-testid="prof-month-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {windows.map((w) => (
                                <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                }
            />

            <div className="px-8 py-6 space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <KPICard testid="prof-kpi-revenue" label="Revenue" value={fmtMoney(totalRevenue)} icon={DollarSign} delta="Paid this month" deltaTone="success" />
                    <KPICard testid="prof-kpi-cost" label="Allocated cost" value={fmtMoney(totalCost)} icon={TrendingDown} delta="Trucks + overhead + carts" />
                    <KPICard testid="prof-kpi-profit" label="Net profit" value={fmtMoney(totalProfit)}
                             icon={totalProfit >= 0 ? TrendingUp : AlertTriangle}
                             deltaTone={totalProfit >= 0 ? "success" : "destructive"}
                             delta={`${marginPct}% margin`} />
                    <KPICard testid="prof-kpi-losers" label="Money-losers" value={losers.length} icon={AlertTriangle}
                             deltaTone={losers.length ? "destructive" : "muted"}
                             delta={losers.length ? "Rate hikes needed" : "None — nice work"} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Card>
                        <CardContent className="p-6">
                            <div className="overline mb-1">Fleet economics</div>
                            <h3 className="font-heading text-lg font-semibold mb-4">Cost per truck</h3>
                            <div className="space-y-3" data-testid="truck-cost-list">
                                {(truckReport?.rows || []).length === 0 && (
                                    <div className="text-sm text-muted-foreground py-6 text-center">
                                        No expenses tagged to a truck this month yet.
                                    </div>
                                )}
                                {(truckReport?.rows || []).map((t) => (
                                    <div key={t.truck_id} className="flex items-center gap-3 p-3 rounded-md bg-secondary/40"
                                         data-testid={`truck-cost-${t.truck_id}`}>
                                        <Truck className="w-4 h-4 text-muted-foreground shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium">{t.truck_name}</div>
                                            <div className="text-[11px] text-muted-foreground">
                                                {t.stops_completed} stops
                                                {t.cost_per_stop != null && ` · ${fmtMoney(t.cost_per_stop)}/stop`}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-sm font-semibold tabular">{fmtMoney(t.monthly_cost)}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="p-6">
                            <div className="overline mb-1">Wall of fame</div>
                            <h3 className="font-heading text-lg font-semibold mb-4">Top 5 profitable customers</h3>
                            <div className="space-y-2" data-testid="top-customers">
                                {winners.length === 0 && (
                                    <div className="text-sm text-muted-foreground py-6 text-center">
                                        Not enough revenue booked yet this month.
                                    </div>
                                )}
                                {winners.map((r, i) => (
                                    <div key={r.customer_id} className="flex items-center gap-3 p-3 rounded-md hover:bg-secondary/40"
                                         data-testid={`top-cust-${r.customer_id}`}>
                                        <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-mono font-bold">
                                            {i + 1}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate">{r.customer_name}</div>
                                            <div className="text-[11px] text-muted-foreground">{fmtMoney(r.revenue)} rev · {fmtMoney(r.total_cost)} cost</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 tabular">{fmtMoney(r.profit)}</div>
                                            {r.margin_pct != null && <div className="text-[10px] text-muted-foreground">{r.margin_pct}% margin</div>}
                                        </div>
                                        {i === 0 && <Trophy className="w-4 h-4 text-amber-500" />}
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="overline mb-1">Every customer</div>
                                <h3 className="font-heading text-lg font-semibold">Per-customer P&amp;L</h3>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Sort tip — money-losers are pinned at the top.
                                    Overhead per customer this month: {fmtMoney(profitReport?.overhead_per_customer || 0)}
                                </p>
                            </div>
                            <div className="flex items-center gap-2 max-w-xs">
                                <Search className="w-4 h-4 text-muted-foreground" />
                                <Input value={search} onChange={(e) => setSearch(e.target.value)}
                                       placeholder="Search customer…" data-testid="prof-search" />
                            </div>
                        </div>
                        <div className="overflow-x-auto" data-testid="profitability-table">
                            <table className="w-full text-sm">
                                <thead>
                                <tr className="border-b border-border surface">
                                    <th className="text-left px-4 py-3 overline">Customer</th>
                                    <th className="text-right px-4 py-3 overline">Revenue</th>
                                    <th className="text-right px-4 py-3 overline">Truck</th>
                                    <th className="text-right px-4 py-3 overline">Overhead</th>
                                    <th className="text-right px-4 py-3 overline">Cart</th>
                                    <th className="text-right px-4 py-3 overline">Total cost</th>
                                    <th className="text-right px-4 py-3 overline">Profit</th>
                                    <th className="text-right px-4 py-3 overline">Margin</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filtered.length === 0 && (
                                    <tr><td colSpan={8} className="text-center py-10 text-sm text-muted-foreground">No customers match your filter.</td></tr>
                                )}
                                {filtered.map((r) => {
                                    const negative = r.profit < 0 && r.revenue > 0;
                                    const noRev = r.revenue === 0;
                                    return (
                                        <tr key={r.customer_id}
                                            className={`border-b border-border hover:bg-secondary/40 ${negative ? "bg-red-500/5" : ""}`}
                                            data-testid={`prof-row-${r.customer_id}`}>
                                            <td className="px-4 py-3">
                                                <div className="font-medium">{r.customer_name}</div>
                                                <div className="text-[11px] text-muted-foreground">{r.stops_completed} stops · {r.service_plan}</div>
                                            </td>
                                            <td className="px-4 py-3 text-right tabular">{fmtMoney(r.revenue)}</td>
                                            <td className="px-4 py-3 text-right tabular text-muted-foreground">{fmtMoney(r.truck_cost)}</td>
                                            <td className="px-4 py-3 text-right tabular text-muted-foreground">{fmtMoney(r.overhead_cost)}</td>
                                            <td className="px-4 py-3 text-right tabular text-muted-foreground">{fmtMoney(r.cart_cost)}</td>
                                            <td className="px-4 py-3 text-right tabular">{fmtMoney(r.total_cost)}</td>
                                            <td className={`px-4 py-3 text-right tabular font-semibold ${
                                                negative ? "text-red-600 dark:text-red-400" :
                                                noRev ? "text-muted-foreground" :
                                                "text-emerald-600 dark:text-emerald-400"
                                            }`}>{fmtMoney(r.profit)}</td>
                                            <td className={`px-4 py-3 text-right tabular ${
                                                negative ? "text-red-600 dark:text-red-400" : ""
                                            }`}>
                                                {r.margin_pct != null ? `${r.margin_pct}%` : "—"}
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>

                <div className="text-[11px] text-muted-foreground">
                    <strong>How costs are allocated:</strong> Truck-scoped expenses (fuel, maintenance, repairs, tires, insurance, registration, wages)
                    are divided by the number of completed stops on each truck this month. Company-wide costs (overhead, office, software) are
                    divided evenly across active customers. Cart cost is straight-line over 84 months ($80 ÷ 84 = ~$0.95/mo).
                </div>
            </div>
        </div>
    );
}
